import React from 'react'
import { colorConstants, marginSizes } from '@jarvis.oddle/core/theme/default'
import Modal, {
  CloseButton,
  PrevButton
} from '@jarvis.oddle/core/components/Modal'
import { H4, LinkText, P } from '@jarvis.oddle/core/components/Typo'
import Flex from '@jarvis.oddle/core/components/Flex'
import Form from '@jarvis.oddle/core/components/Form'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import {
  storeOrderToDeliveryAddress,
  toDeliveryAddress
} from '@jarvis.oddle/core/tools/address'
import { getEmptyPasswordError } from '@jarvis.oddle/core/tools/validation'
import IconOddlePass from '@jarvis.oddle/core/components/Icons/IconOddlePass'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import PoweredByOddlePass, {
  UTMSource
} from 'src/components/molecules/PoweredByOddlePass'
import loginFlow from 'src/flows/loginFlow'
import UserContext from 'src/containers/UserContext'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'
import { selectDetailMenuSite } from 'src/redux/meta'
import { makeControlledModal } from 'src/decorators/ModalController'
import {
  createThenSetUserDefaultAddressAction,
  fetchOcaUserAction,
  loginAction
} from 'src/redux/user'
import { syncDeliveryLocationAction } from 'src/redux/order/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'

const LogInModal = ({
  modal,
  email,
  mcaExist
}: {
  modal: {
    show: boolean
    onClose: (handler?) => void
    onClosed: () => void
  }
  email: string
  mcaExist: boolean
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const { t } = React.useContext(TranslationContext)
  const { isOddlePassEnabled } = React.useContext(UserContext)
  const { storeOrder } = useAppSelector(selectOrder)
  const { menu } = useAppSelector(selectDetailMenuSite) || {}

  const goToPasswordModal = React.useCallback(async () => {
    loginFlow.send('passwordResetConfirmation', { email })
  }, [email])

  const loginUser = async ({ password }, { setFieldError, setSubmitting }) => {
    const loginResponse = await dispatch(
      loginAction({ email, password, source: null })
    )
    setSubmitting(false)
    if (loginResponse.meta.requestStatus === 'fulfilled') {
      const user = loginResponse.payload
      const userName = `${user.firstName} ${user.lastName}`
      showNotificationToast.success(t('Welcome back _userName_', { userName }))
      if (storeOrder.data.deliveryAddress) {
        await dispatch(
          createThenSetUserDefaultAddressAction(
            storeOrderToDeliveryAddress(storeOrder.data)
          )
        )
        loginFlow.finish({ skipAddress: true })
      } else if (!storeOrder.data.isPickup && user.preferredDeliveryAddress) {
        const syncDeliveryLocationResponse = await dispatch(
          syncDeliveryLocationAction({
            address: toDeliveryAddress(user.preferredDeliveryAddress)
          })
        )
        loginFlow.finish({ skipAddress: syncDeliveryLocationResponse.meta.requestStatus === 'fulfilled' })
      } else {
        loginFlow.finish()
      }
    } else {
      const { error } = loginResponse.payload
      if (error === 'customer_email_banned') {
        const merchant = (
          <a target='_blank' href='/stores'>
            <LinkText color={colorConstants.red}>{menu?.name}</LinkText>
          </a>
        )
        setFieldError(
          'password',
          t('Account has been suspended. Please contact _merchant_ for help.', {
            merchant
          })
        )
      } else {
        setFieldError('password', t('Password is invalid'))
      }
    }
  }

  const createMcaFlow = async (
    { password },
    { setFieldError, setSubmitting }
  ) => {
    const response = await dispatch(fetchOcaUserAction({ email, password }))
    setSubmitting(false)
    if (response.meta.requestStatus === 'fulfilled') {
      const { hash, customer } = response.payload
      loginFlow.send('signUp', {
        hash,
        email,
        ocaExist: true,
        prefilledData: {
          phone: customer.phone,
          firstName: customer.firstName,
          lastName: customer.lastName,
          receiveEmail: true
        }
      })
    } else {
      setFieldError('password', t('Password is invalid'))
    }
  }

  const onFormSubmit = async (values, actions) => {
    if (mcaExist) {
      await loginUser(values, actions)
    } else {
      await createMcaFlow(values, actions)
    }
  }

  const onFormValidate = (values) => {
    const errors = {} as {
      password: string
    }

    const { password } = values
    const passwordError = getEmptyPasswordError(t, password)
    if (passwordError) errors.password = passwordError

    return errors
  }

  const resetYourPasswordLink = (
    <LinkText
      key="resetPassword"
      marginTop={marginSizes.large}
      marginBottom={marginSizes.small}
      onClick={goToPasswordModal}
      heading
    >
      {t('reset your password')}
    </LinkText>
  )

  return (
    <Modal {...modal} slideInAnimation='top'>
      <Modal.FlexContainer>
        <Modal.Header>
          <PrevButton onClick={() => loginFlow.prev()} />
          {isOddlePassEnabled ? (
            <IconOddlePass />
          ) : (
            <H4 bold normalised>
              {t('Welcome Back!')}
            </H4>
          )}
          <CloseButton onClick={modal.onClose} />
        </Modal.Header>
        <Modal.Body withoutFooter>
          <Modal.Section flexGrow={1}>
            {isOddlePassEnabled ? (
              <H4 normalised marginBottom={marginSizes.small} bold>
                {t(
                  "You have ordered food from Oddle's restaurant partner before. Simply use the same password to log in."
                )}
              </H4>
            ) : (
              <H4 normalised marginBottom={marginSizes.small} bold>
                {t('Enter your password to login')}
              </H4>
            )}
            <Form
              initialValues={{ password: '' }}
              onSubmit={onFormSubmit}
              onValidate={onFormValidate}
            >
              <Form.Password
                name='password'
                label={t('Your Password')}
                fieldValidateOnBlur={false}
              />
              <Form.SubmitButton fullWidth>{t('Log in')}</Form.SubmitButton>
            </Form>
            <Flex justifyContent='center'>
              <P textAlign='center'>
                {t(
                  'If you have trouble logging in, please _resetYourPassword_.',
                  {
                    resetYourPassword: resetYourPasswordLink
                  }
                )}
              </P>
            </Flex>
            <PoweredByOddlePass
              UTMSource={UTMSource.SIGNIN_MODAL}
              copyText={t('_email_ is your Oddle Pass login.', { email })}
            />
          </Modal.Section>
        </Modal.Body>
      </Modal.FlexContainer>
    </Modal>
  )
}

export default makeControlledModal(LogInModal, 'LogInModal')
